import React, {Fragment, useEffect, useMemo, useState} from 'react';

import {DragDropContext} from 'react-beautiful-dnd';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useUpdateCurrentUser} from 'app/current-user/api';
import StateButton from 'app/general/widgets/StateButton';
import {useNotifications} from 'app/toasts/hooks';
import {gt} from 'app/utils/i18n';

import {DroppableNavList} from './DroppableNavList';

import {addMainField, AnnotatedNavLinkType, useNavLinks} from '../nav_links';

interface Props {
  show: boolean;
  onClose: () => void;
}

export function ReorderNavTabsModal({
  show,
  onClose,
}: Props): JSX.Element | null {
  const [mounted, setMounted] = useState(false);
  const navLinks = useNavLinks();
  const [updateCurrentUser] = useUpdateCurrentUser();
  const [navSlugs, setNavSlugs] = useState(navLinks.map((l) => l.slug));
  const {showToastNotification} = useNotifications();

  function onReorder(sourceIndex: number, destinationIndex: number) {
    setNavSlugs(moveInList(navSlugs, sourceIndex, destinationIndex));
  }

  const navMap = useMemo(() => {
    // create a dict of links with slug as key
    // to efficiently get any link by slug
    let navMap = {};

    for (const item of navLinks) {
      navMap[item.slug] = item;
    }

    return navMap;
  }, [navLinks]);
  const localNavLinks = useMemo<Array<AnnotatedNavLinkType>>(
    () => addMainField(navSlugs.map((slug) => navMap[slug])),
    [navSlugs, navMap]
  );
  // user changed the current order
  const orderChanged = useMemo(
    () => navSlugs.join(',') !== navLinks.map((l) => l.slug).join(','),
    [navSlugs, navLinks]
  );
  useEffect(() => {
    if (show) {
      setMounted(true);
    }
  }, [show]);

  function updateNavLinks() {
    if (!orderChanged) {
      return Promise.resolve(onClose());
    }

    return updateCurrentUser({
      navigation_links: navSlugs.join(','),
    })
      .unwrap()
      .then(onClose, () =>
        showToastNotification(
          gt("Couldn't update your navigation links at the moment"),
          {
            level: 'error',
          }
        )
      );
  }

  // lazy render the modal
  // the modal component will be rendered only when visible
  if (!mounted) {
    return null;
  }

  const lists = (
    <Fragment>
      <DroppableNavList
        title={gt('Main Tabs')}
        listId="main"
        navLinks={localNavLinks.filter((l) => l.isMain)}
        main={true}
        dragEnabled={show}
      />
      <DroppableNavList
        title={gt('Under "More" Menu')}
        listId="more"
        navLinks={localNavLinks.filter((l) => !l.isMain)}
        main={false}
        dragEnabled={show}
      />
    </Fragment>
  );
  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      onClosed={() => {
        // reset non-saved custom order on modal close
        setNavSlugs(navLinks.map((l) => l.slug));
        // stop rendering TabbedModal
        setMounted(false);
      }}
      tabs={[
        {
          eventKey: 'customise',
          name: gt('Customize Navigation'),
          icon: 'far fa-compass',
          content: (
            <div>
              <p className="lead">
                {gt(
                  'Drag and drop the navigation buttons below to set a new order for the navigation tabs'
                )}
                .
              </p>
              {show ? (
                <DragDropContext
                  onDragEnd={(result) => {
                    const {source, destination} = result;

                    // dropped outside the list
                    if (!destination) {
                      return;
                    }

                    const sourceIndex =
                      source.index + (source.droppableId === 'more' ? 4 : 0);
                    const destinationIndex =
                      destination.index +
                      (destination.droppableId === 'more' ? 4 : 0);
                    onReorder(sourceIndex, destinationIndex);
                  }}
                >
                  {lists}
                </DragDropContext>
              ) : (
                lists
              )}
              <StyledModalFooter>
                <button
                  className="btn btn-sm btn-secondary mr-auto"
                  onClick={onClose}
                >
                  {gt('Cancel')}
                </button>
                <StateButton
                  className="btn btn-sm btn-primary px-3"
                  busyText={gt('Confirm')}
                  onClick={updateNavLinks}
                >
                  {gt('Confirm')}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
}

function moveInList<T>(
  list: Array<T>,
  sourceIndex: number,
  targetIndex: number
): Array<T> {
  const newList = [...list];
  newList.splice(sourceIndex, 1);
  newList.splice(targetIndex, 0, list[sourceIndex]);
  return newList;
}
